import dayjs from 'dayjs';

/**
 * Design System v2 period presets (Figma "Filters. Key Details"):
 * This month / Previous month / This year / Previous year / All time /
 * Custom. Ranges use full calendar bounds so stepping and labels stay on
 * month/year boundaries.
 */
export const PRESET_DEFS = {
    this_month: {
        label: 'This month',
        range: () => ({
            start: dayjs().startOf('month'),
            end: dayjs().endOf('month'),
        }),
    },
    prev_month: {
        label: 'Previous month',
        range: () => ({
            start: dayjs().subtract(1, 'month').startOf('month'),
            end: dayjs().subtract(1, 'month').endOf('month'),
        }),
    },
    this_year: {
        label: 'This year',
        range: () => ({
            start: dayjs().startOf('year'),
            end: dayjs().endOf('year'),
        }),
    },
    prev_year: {
        label: 'Previous year',
        range: () => ({
            start: dayjs().subtract(1, 'year').startOf('year'),
            end: dayjs().subtract(1, 'year').endOf('year'),
        }),
    },
    all: {
        label: 'All time',
        range: () => ({ start: null, end: null }),
    },
};

export const PRESET_ORDER = [
    'this_month',
    'prev_month',
    'this_year',
    'prev_year',
    'all',
];

const isFullMonthRange = (start, end) =>
    start.isSame(start.startOf('month'), 'day') &&
    end.isSame(start.endOf('month'), 'day');

const isFullYearRange = (start, end) =>
    start.isSame(start.startOf('year'), 'day') &&
    end.isSame(start.endOf('year'), 'day');

const isMonthBoundedRange = (start, end) =>
    start.isSame(start.startOf('month'), 'day') &&
    end.isSame(end.endOf('month'), 'day');

/**
 * Human trigger label for a period (per the v2 examples: "May 2026",
 * "2025", "Jan 2025 – May 2026", "All time", else full dates).
 *
 * @param {import('dayjs').Dayjs|null} start
 * @param {import('dayjs').Dayjs|null} end
 */
export function labelForRange(start, end) {
    if (!start || !end) {
        return 'All time';
    }

    if (isFullYearRange(start, end)) {
        return start.format('YYYY');
    }

    if (isFullMonthRange(start, end)) {
        return start.format('MMM YYYY');
    }

    if (isMonthBoundedRange(start, end)) {
        return `${start.format('MMM YYYY')} – ${end.format('MMM YYYY')}`;
    }

    const sameYear = start.year() === end.year();

    return sameYear
        ? `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`
        : `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
}

/**
 * Step a range by its own granularity: full months shift by months, full
 * years by years, anything else by its day span.
 *
 * @returns {{start: import('dayjs').Dayjs, end: import('dayjs').Dayjs}}
 */
export function stepRange(start, end, direction) {
    if (isFullYearRange(start, end)) {
        const next = start.add(direction, 'year');

        return { start: next.startOf('year'), end: next.endOf('year') };
    }

    if (isFullMonthRange(start, end)) {
        const next = start.add(direction, 'month');

        return { start: next.startOf('month'), end: next.endOf('month') };
    }

    if (isMonthBoundedRange(start, end)) {
        const months = end.diff(start, 'month') + 1;
        const nextStart = start.add(direction * months, 'month');
        const nextEnd = nextStart
            .add(months - 1, 'month')
            .endOf('month');

        return { start: nextStart.startOf('month'), end: nextEnd };
    }

    const days = end.diff(start, 'day') + 1;

    return {
        start: start.add(direction * days, 'day'),
        end: end.add(direction * days, 'day'),
    };
}
